<script setup>
    defineProps({
        phase: {
            type: String,
            required: true
        },
        finalState: {
            type: String,
            default: null
        },
        error: {
            type: String,
            default: null
        },
        polling: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['retry']);

    const onRetry = () => {
        emit('retry');
    };
</script>

<template>
    <div
        data-test="instant-credit-status"
        class="tw:bg-white tw:rounded-2xl tw:border tw:border-site/15 tw:overflow-hidden tw:shadow-sm"
    >
        <!-- Pending / Processing -->
        <div
            v-if="phase === 'pending' || polling"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-6 tw:py-12"
        >
            <div
                class="tw:relative tw:w-16 tw:h-16 tw:mb-6"
                aria-hidden="true"
            >
                <div
                    class="tw:absolute tw:inset-0 tw:border-4 tw:border-site/10 tw:rounded-full"
                ></div>
                <div
                    class="tw:absolute tw:inset-0 tw:border-4 tw:border-transparent tw:border-t-alt tw:rounded-full tw:animate-spin"
                ></div>
            </div>
            <h3
                class="tw:text-lg tw:font-bold tw:uppercase tw:tracking-wide tw:text-site tw:mb-2"
            >
                {{ $t('instant_credit.processing') }}
            </h3>
            <p class="tw:text-sm tw:text-site/60 tw:max-w-xs tw:leading-relaxed">
                {{ $t('instant_credit.processing_detail') }}
            </p>
        </div>

        <!-- Failed -->
        <div
            v-else-if="finalState === 'failed'"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-6 tw:py-12"
        >
            <div
                class="tw:w-16 tw:h-16 tw:bg-red-50 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:mb-6"
            >
                <svg
                    class="tw:w-8 tw:h-8 tw:text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M15 9l-6 6M9 9l6 6" />
                </svg>
            </div>
            <h3
                class="tw:text-lg tw:font-bold tw:uppercase tw:tracking-wide tw:text-red-600 tw:mb-2"
            >
                {{ $t('instant_credit.failed') }}
            </h3>
            <p
                v-if="error"
                class="tw:text-xs tw:text-site/50 tw:font-mono tw:mb-4 tw:max-w-xs tw:break-all"
            >
                {{ error }}
            </p>
            <button
                type="button"
                data-test="instant-credit-retry"
                class="tw:mt-2 tw:py-3 tw:px-6 tw:bg-site tw:text-white tw:font-bold tw:uppercase tw:tracking-[0.15em] tw:rounded-md tw:text-xs hover:tw:bg-black tw:transition-colors focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-site focus-visible:tw:ring-offset-2"
                @click="onRetry"
            >
                {{ $t('instant_credit.retry') }}
            </button>
        </div>

        <!-- Cancelled -->
        <div
            v-else-if="finalState === 'cancelled'"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-6 tw:py-12"
        >
            <div
                class="tw:w-16 tw:h-16 tw:bg-amber-50 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:mb-6"
            >
                <svg
                    class="tw:w-8 tw:h-8 tw:text-amber-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path
                        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            </div>
            <h3
                class="tw:text-lg tw:font-bold tw:uppercase tw:tracking-wide tw:text-amber-600 tw:mb-2"
            >
                {{ $t('instant_credit.cancelled') }}
            </h3>
            <button
                type="button"
                data-test="instant-credit-retry"
                class="tw:mt-2 tw:py-3 tw:px-6 tw:bg-site tw:text-white tw:font-bold tw:uppercase tw:tracking-[0.15em] tw:rounded-md tw:text-xs hover:tw:bg-black tw:transition-colors focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-site focus-visible:tw:ring-offset-2"
                @click="onRetry"
            >
                {{ $t('instant_credit.retry') }}
            </button>
        </div>

        <!-- Refunded -->
        <div
            v-else-if="finalState === 'refunded'"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-6 tw:py-12"
        >
            <div
                class="tw:w-16 tw:h-16 tw:bg-blue-50 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:mb-6"
            >
                <svg
                    class="tw:w-8 tw:h-8 tw:text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
            </div>
            <h3
                class="tw:text-lg tw:font-bold tw:uppercase tw:tracking-wide tw:text-blue-600 tw:mb-2"
            >
                {{ $t('instant_credit.refunded') }}
            </h3>
        </div>

        <!-- Not Found -->
        <div
            v-else-if="finalState === 'not_found'"
            class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:px-6 tw:py-12"
        >
            <div
                class="tw:w-16 tw:h-16 tw:bg-site/5 tw:rounded-full tw:flex tw:items-center tw:justify-center tw:mb-6"
            >
                <svg
                    class="tw:w-8 tw:h-8 tw:text-site/50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            </div>
            <h3
                class="tw:text-lg tw:font-bold tw:uppercase tw:tracking-wide tw:text-site/70 tw:mb-2"
            >
                {{ $t('instant_credit.not_found') }}
            </h3>
        </div>
    </div>
</template>
